/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useMemo } from 'react'
import * as THREE from 'three'
import { useFrame } from '@react-three/fiber'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { frame } from '../flight/state.ts'
import { makeMatcap } from '../assets/matcap.ts'
import { loadTexture } from '../assets/loaders.ts'
import { HASH } from '../shaders/chunks.ts'
import { mulberry32 } from '../prng.ts'
import { DESKS } from '../world.ts'
import { Q } from '../quality.ts'
import { glsl, INK } from '../palette.ts'

// FOUNDATION §6.3 near LOD: extruded letterpress type with real thickness, matcap metal.
// Six procedural slab-glyph variants (no font outlines exist in the repo — ISSUES.md #2),
// ~70 instances each. Same stateless motion idea as the far field, minus the tube mode:
// near glyphs tumble through the early acts, snap to desk-top ranks at ORGANIZE, dissolve
// at SKY (simplification documented in ISSUES.md #7).

const VERT = /* glsl */ `
attribute float aSeed;
attribute float aIndex;
attribute vec3 aHome;   // this glyph's monitor; also where it lands as cast type
uniform float uTime, uFlow, uActive, uSpread, uCalm, uFog, uPool;
uniform vec3 uBg;
varying vec3 vN;
varying float vFo;
varying float vVis;
${HASH}
mat3 rotXY(float ax, float ay) {
  float cx = cos(ax), sx = sin(ax), cy = cos(ay), sy = sin(ay);
  return mat3(cy, 0.0, -sy, 0.0, 1.0, 0.0, sy, 0.0, cy) *
         mat3(1.0, 0.0, 0.0, 0.0, cx, sx, 0.0, -sx, cx);
}
void main() {
  vec3 r = hash31(aSeed);
  vec3 r2 = hash31(aSeed + 9.9);

  vec2 origin = mix(vec2(1.72, 0.12), aHome.xz, clamp(uSpread, 0.0, 1.0));
  float speed = 0.4 + r2.x * 0.7;
  float h = mix(9.0, 14.0, uSpread) * uCalm;
  float y0 = mod(r.y * h + uTime * speed, h);
  float sw = (0.16 + y0 * 0.1) * (0.4 + 0.6 * uSpread);
  vec3 p0 = vec3(origin.x + sin(y0 * 0.5 + aSeed * 6.283) * sw, 1.05 + y0,
                 origin.y + cos(y0 * 0.44 + aSeed * 4.7) * sw);

  // cast type lands on its own desk top at the snap
  vec3 p2 = vec3(aHome.x + (r.x - 0.5) * 0.8, 0.95, aHome.z + 0.3 + (r.z - 0.5) * 0.4);

  float y3 = mod(r.y * 80.0 + uTime * (2.0 + 2.0 * r2.x), 80.0);
  vec3 p3 = vec3(p2.x, 2.0 + y3 * 1.4, p2.z);
  float fade3 = 1.0 - smoothstep(45.0, 110.0, 2.0 + y3 * 1.4);

  float fs = uFlow + (r2.y - 0.5) * 0.3;
  float m2 = smoothstep(1.05, 1.8, fs);
  float m3 = smoothstep(2.05, 2.8, fs);
  vec3 wp = mix(mix(p0, p2, m2), p3, m3);

  float act = step(aIndex / uPool, uActive);
  float sc = (0.7 + r2.z * 0.8) * act;

  float spin = 1.0 - m2 * 0.95;
  mat3 R = rotXY((uTime * (0.4 + r.x) + aSeed) * spin, (uTime * (0.5 + r.y) + aSeed * 2.0) * spin + m2 * 3.1416);
  vec3 tp = R * (position * sc) + wp;
  vN = normalize(normalMatrix * (R * normal));

  vec4 mv = viewMatrix * vec4(tp, 1.0);
  gl_Position = projectionMatrix * mv;
  float dist = length(mv.xyz);
  vFo = 1.0 - exp(-uFog * uFog * dist * dist);
  vVis = act * mix(1.0, fade3, m3);
}
`

const FRAG = /* glsl */ `
uniform sampler2D uMatcap;
uniform vec3 uBg;
varying vec3 vN;
varying float vFo;
varying float vVis;
void main() {
  vec2 muv = normalize(vN).xy * 0.485 + 0.5;
  vec3 metal = texture2D(uMatcap, muv).rgb;
  vec3 col = metal * mix(vec3(1.0), ${glsl(INK)} * 4.0, 0.35);
  float lum = dot(col, vec3(0.299, 0.587, 0.114));
  col = mix(col, vec3(lum), clamp(vFo * 1.7, 0.0, 1.0));
  col = mix(col, uBg, vFo);
  col = mix(uBg, col, vVis);
  gl_FragColor = vec4(col, 1.0);
}
`

function slabGlyph(seed: number): THREE.BufferGeometry {
  const r = mulberry32(seed)
  const parts: THREE.BufferGeometry[] = []
  const t = 0.055 // cast depth
  const stroke = 0.05
  const H = 0.28
  const box = (w: number, h: number, x: number, y: number) => {
    const g = new THREE.BoxGeometry(w, h, t)
    g.translate(x, y, 0)
    parts.push(g)
  }
  box(stroke, H, (r() - 0.5) * 0.06, 0) // spine
  const n = 1 + Math.floor(r() * 2)
  for (let i = 0; i < n; i++) box(0.1 + r() * 0.13, stroke, 0.06 + r() * 0.05, (r() - 0.5) * H * 0.8)
  if (r() < 0.55) box(stroke * 1.5, stroke * 1.5, (r() - 0.5) * 0.22, (r() - 0.5) * H)
  if (r() < 0.4) box(stroke, H * 0.55, 0.14 + r() * 0.05, (r() - 0.5) * H * 0.4) // second stem
  return mergeGeometries(parts)!
}

export function GlyphsNear() {
  const { meshes, material } = useMemo(() => {
    const VARIANTS = 6
    const perVariant = Math.floor(Q.nearGlyphs / VARIANTS)
    const material = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uMatcap: { value: loadTexture('/assets/matcap.png', makeMatcap) },
        uTime: { value: 0 },
        uFlow: { value: 0 },
        uActive: { value: 0.02 },
        uSpread: { value: 0 },
        uCalm: { value: 1 },
        uFog: { value: 0.016 },
        uPool: { value: Q.nearGlyphs },
        uBg: { value: new THREE.Color() },
      },
    })
    const rand = mulberry32(1313)
    const meshes: { geometry: THREE.InstancedBufferGeometry }[] = []
    for (let v = 0; v < VARIANTS; v++) {
      const base = slabGlyph(v * 37 + 5)
      const geo = new THREE.InstancedBufferGeometry()
      geo.index = base.index
      geo.setAttribute('position', base.attributes.position)
      geo.setAttribute('normal', base.attributes.normal)
      const seeds = new Float32Array(perVariant)
      const idx = new Float32Array(perVariant)
      const homes = new Float32Array(perVariant * 3)
      // near glyphs belong to desks near the camera path, where the metal reads
      const nearDesks = DESKS.filter((d) => Math.abs(d.x) < 32 && d.z > -14 && d.z < 62)
      for (let i = 0; i < perVariant; i++) {
        seeds[i] = rand() * 120
        idx[i] = v * perVariant + i
        const d = nearDesks[Math.floor(rand() * nearDesks.length)]
        homes[i * 3] = d.x
        homes[i * 3 + 1] = 1.2
        homes[i * 3 + 2] = d.z
      }
      geo.setAttribute('aSeed', new THREE.InstancedBufferAttribute(seeds, 1))
      geo.setAttribute('aIndex', new THREE.InstancedBufferAttribute(idx, 1))
      geo.setAttribute('aHome', new THREE.InstancedBufferAttribute(homes, 3))
      geo.instanceCount = perVariant
      meshes.push({ geometry: geo })
    }
    return { meshes, material }
  }, [])

  useFrame((st) => {
    const b = frame.beats
    const u = material.uniforms
    u.uTime.value = st.clock.elapsedTime
    u.uFlow.value = b.flow
    u.uActive.value = b.active
    u.uSpread.value = b.spread
    u.uCalm.value = b.calm
    u.uFog.value = b.fog
    ;(u.uBg.value as THREE.Color).copy(frame.bg)
  })

  return (
    <>
      {meshes.map((m, i) => (
        <mesh key={i} geometry={m.geometry} material={material} frustumCulled={false} />
      ))}
    </>
  )
}
